import {
  ComponentMultiStyleConfig,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { ComponentStyleProps } from "../../../../../lib/styles/props/component-style-props";
import { hasValue } from "../../../../../lib/util/common-util";
import { CMFormCommentMain, CMFormCommentReply } from "../index";

export type CMEditRequestThreadProps = {
  /**
   * メインコメント・返信コメントの編集ボタンのラベル・Ariaラベル。
   * 編集可能とする場合、設定を強く推奨する。
   */
  editButtonLabel?: string;
  /**
   * テキストエリアの最大文字数。
   * 指定しなければ無制限。
   */
  maxLength?: number;

  commentInput: {
    main: {
      placeholder?: string;
      finishButtonLabel: string;
      cancelButtonLabel: string;
      deleteButtonLabel: string;
    };
    reply: {
      placeholder?: string;
      finishButtonLabel: string;
      cancelButtonLabel: string;
      deleteButtonLabel: string;
    };
  };

  deleteConfirmationModal: {
    title: string;
    message?: string;
    deleteButtonLabel: string;
    cancelButtonLabel: string;
  };

  /**
   * 削除確認モーダルの制御に使用する。
   */
  deleteConfirmationModalSettings: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };

  mainComment?: {
    userName: string;
    date: Date;
    comment: string;
  };
  replyComment?: {
    userName: string;
    date: Date;
    comment: string;
  };

  /**
   * メインコメントが保存・更新されたときのコールバック。
   * この関数が存在すれば、返信コメント編集可能とする。
   *
   * nullに更新する場合、削除を表す。
   */
  onSaveMain?: (value: string | null) => void;
  /**
   * 返信コメントが保存・更新されたときのコールバック。
   * この関数が存在すれば、返信コメント編集可能とする。
   *
   * nullに更新する場合、削除を表す。
   */
  onSaveReply?: (value: string | null) => void;
} & ComponentStyleProps;

/**
 * 修正依頼コメントリストを表示するコンポーネント
 */
export const CMEditRequestThread = ({
  editButtonLabel,
  maxLength,

  commentInput,

  deleteConfirmationModal,
  deleteConfirmationModalSettings,

  mainComment,
  replyComment,

  onSaveMain,
  onSaveReply,

  sx,
  ...rest
}: CMEditRequestThreadProps) => {
  const styles = useMultiStyleConfig("CMEditRequestThreadStyles");

  const mainEditable = useMemo(() => hasValue(onSaveMain), [onSaveMain]);
  const replyEditable = useMemo(() => hasValue(onSaveReply), [onSaveReply]);

  return (
    <VStack sx={{ ...styles.container, ...sx }} {...rest}>
      {hasValue(mainComment) || mainEditable ? (
        <CMFormCommentMain
          editButtonLabel={editButtonLabel}
          maxLength={maxLength}
          commentInput={commentInput.main}
          deleteConfirmationModal={deleteConfirmationModal}
          deleteConfirmationModalSettings={deleteConfirmationModalSettings}
          content={mainComment}
          editable={mainEditable}
          onSave={onSaveMain}
          sx={styles.main}
        />
      ) : null}
      {hasValue(mainComment) && (hasValue(replyComment) || replyEditable) && (
        <CMFormCommentReply
          editButtonLabel={editButtonLabel}
          maxLength={maxLength}
          commentInput={commentInput.reply}
          deleteConfirmationModal={deleteConfirmationModal}
          deleteConfirmationModalSettings={deleteConfirmationModalSettings}
          content={replyComment}
          editable={replyEditable}
          onSave={onSaveReply}
          sx={styles.reply}
        />
      )}
    </VStack>
  );
};

export const CMEditRequestThreadStyles: ComponentMultiStyleConfig = {
  parts: ["container", "main", "reply"],
  baseStyle: {
    container: {},
    main: {
      alignSelf: "stretch",
    },
    reply: {
      alignSelf: "stretch",
    },
  },
};
